import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { emptyLoanPaymentDraft, LoanPaymentMethodFields } from "@/components/hr/LoanPaymentMethodFields";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { getEmployeeById, listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  createEmployeeLoan,
  loansApiErrorMessage,
  updateEmployeeLoan,
  validateLoanPayment,
  type EmployeeLoanRecord,
} from "@/lib/hr/loansStore";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";
import { addMonths, format, isValid, parseISO } from "date-fns";
import { Calculator, Check, CheckCircle2, ChevronsUpDown, HandCoins, Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type Draft = {
  loanAmount: string;
  installmentAmount: string;
  startDate: string;
  paymentMethod: ReturnType<typeof emptyLoanPaymentDraft>["paymentMethod"];
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
};

const emptyDraft = (): Draft => ({
  loanAmount: "",
  installmentAmount: "",
  startDate: format(new Date(), "yyyy-MM-dd"),
  ...emptyLoanPaymentDraft(),
});

function formatMoney(value: string): string {
  const raw = value.replace(/[^\d.]/g, "");
  if (!raw) return value;
  const [whole, ...rest] = raw.split(".");
  const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
  return rest.length ? `${formatted}.${rest.join("")}` : formatted;
}

function safeNumber(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const raw = String(v).replace(/[^\d.]/g, "");
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function commaFormat(n: number | null | undefined): string {
  if (n == null) return "0";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

function employeeDisplayName(e: EmployeeRecord): string {
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeeLabel(e: EmployeeRecord): string {
  const n = employeeDisplayName(e);
  return e.employeeCode ? `${e.employeeCode} · ${n}` : n;
}

function employeeInitials(e: EmployeeRecord): string {
  const first = (e.firstName || "").trim();
  const last = (e.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (e.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

function employeeSubtitle(e: EmployeeRecord): string {
  const parts: string[] = [];
  if (e.departmentName?.trim()) parts.push(e.departmentName.trim());
  if (e.designationName?.trim()) parts.push(e.designationName.trim());
  return parts.join(" · ") || "";
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

export type EmployeeLoanCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, employee is locked (employee profile). Omit on /payroll/loans so the user can pick one. */
  employeeId?: string;
  /** When set, the dialog edits this loan instead of creating a new one. */
  loan?: EmployeeLoanRecord | null;
  onSaved?: () => void;
};

export function EmployeeLoanCreateDialog({
  open,
  onOpenChange,
  employeeId,
  loan,
  onSaved,
}: EmployeeLoanCreateDialogProps) {
  const editing = loan ?? null;
  const lockedEmployeeId = editing?.employeeId || employeeId || "";
  const allowEmployeeSelect = !lockedEmployeeId;

  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(lockedEmployeeId);
  const [saving, setSaving] = useState(false);
  const [employee, setEmployee] = useState<EmployeeRecord | undefined>(() =>
    getEmployeeById(lockedEmployeeId || selectedEmployeeId),
  );
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [empTick, setEmpTick] = useState(0);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);

  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  useEffect(() => {
    if (!open) return;

    if (editing) {
      setSelectedEmployeeId(editing.employeeId);
      setEmployee(getEmployeeById(editing.employeeId));
      setDraft({
        loanAmount: formatMoney(editing.loanAmount),
        installmentAmount: formatMoney(editing.installmentAmount),
        startDate: editing.startDate,
        paymentMethod: editing.paymentMethod,
        bankId: editing.bankId,
        referenceNo: editing.referenceNo,
        chequeNo: editing.chequeNo,
        chequeDate: editing.chequeDate,
      });
      return;
    }

    setDraft(emptyDraft());
    setSelectedEmployeeId(employeeId || "");
    setEmployee(employeeId ? getEmployeeById(employeeId) : undefined);
  }, [open, employeeId, loan?.id]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    void fetchBanks()
      .then((rows) => {
        if (mounted) setBanks(rows);
      })
      .catch(() => {
        if (mounted) setBanks([]);
      });
    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !allowEmployeeSelect) return;
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, [open, allowEmployeeSelect]);

  useEffect(() => {
    if (!selectedEmployeeId) {
      setEmployee(undefined);
      return;
    }
    setEmployee(getEmployeeById(selectedEmployeeId) ?? employees.find((e) => e.id === selectedEmployeeId));
  }, [selectedEmployeeId, employees]);

  const loanNum = useMemo(() => safeNumber(draft.loanAmount), [draft.loanAmount]);
  const instNum = useMemo(() => safeNumber(draft.installmentAmount), [draft.installmentAmount]);

  const installmentsCount = useMemo(() => {
    if (!loanNum || !instNum) return null;
    return Math.max(1, Math.ceil(loanNum / instNum));
  }, [loanNum, instNum]);

  const completionDateFormatted = useMemo(() => {
    if (!installmentsCount || !draft.startDate.trim()) return null;
    const dt = parseISO(draft.startDate);
    if (!isValid(dt)) return null;
    const endDt = addMonths(dt, installmentsCount);
    return format(endDt, "MMM yyyy");
  }, [installmentsCount, draft.startDate]);

  const basicSalaryNum = employee?.basicSalary ? Number(String(employee.basicSalary).replace(/,/g, "")) : 0;
  const employeeName = employee
    ? employee.fullName || `${employee.firstName} ${employee.lastName}`.trim() || "Employee"
    : "Employee";
  const employeeInitial = employeeName.trim().charAt(0).toUpperCase() || "E";

  const applyTenurePreset = (months: number) => {
    if (!loanNum) {
      toast.error("Please enter a loan amount first.");
      return;
    }
    setDraft((prev) => ({ ...prev, installmentAmount: formatAmountCommas(String(Math.round(loanNum / months))) }));
  };

  const applyAmountPreset = (amt: number) => {
    setDraft((prev) => ({
      ...prev,
      loanAmount: formatAmountCommas(String(amt)),
      installmentAmount: prev.installmentAmount.trim()
        ? prev.installmentAmount
        : formatAmountCommas(String(Math.round(amt / 6))),
    }));
  };

  const close = () => onOpenChange(false);

  const save = async () => {
    if (saving) return;
    const resolvedEmployeeId = selectedEmployeeId || lockedEmployeeId;
    if (!resolvedEmployeeId) return toast.error("Select an employee.");
    if (!loanNum || loanNum <= 0) return toast.error("Loan amount must be a number > 0.");
    if (!instNum || instNum <= 0) return toast.error("Installment amount must be a number > 0.");
    if (!draft.startDate.trim()) return toast.error("Start date is required.");
    const paymentError = validateLoanPayment(draft);
    if (paymentError) return toast.error(paymentError);

    setSaving(true);
    try {
      if (editing) {
        const result = await updateEmployeeLoan({
          employeeId: resolvedEmployeeId,
          id: editing.id,
          patch: {
            installmentAmount: instNum,
            startDate: draft.startDate,
            paymentMethod: draft.paymentMethod,
            bankId: draft.bankId,
            referenceNo: draft.referenceNo,
            chequeNo: draft.chequeNo,
            chequeDate: draft.chequeDate,
          },
        });
        toast.success(result.message);
      } else {
        const result = await createEmployeeLoan({
          employeeId: resolvedEmployeeId,
          loanAmount: loanNum,
          installmentAmount: instNum,
          startDate: draft.startDate,
          paymentMethod: draft.paymentMethod,
          bankId: draft.bankId,
          referenceNo: draft.referenceNo,
          chequeNo: draft.chequeNo,
          chequeDate: draft.chequeDate,
        });
        toast.success(result.message);
      }
      close();
      onSaved?.();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">
                {editing ? "Edit Employee Loan" : "Issue Employee Loan"}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                {editing
                  ? "Update the monthly installment, first deduction date, and disbursement method."
                  : "Sanction a company loan with automated monthly payroll installment deductions."}
              </p>
            </div>
          </div>
        </DialogHeader>

        {allowEmployeeSelect ? (
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-foreground">
              Employee <span className="text-destructive">*</span>
            </Label>
            <Popover
              modal
              open={employeePickerOpen}
              onOpenChange={(next) => {
                setEmployeePickerOpen(next);
                if (next) setEmployeePickerCycle((c) => c + 1);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={employeePickerOpen}
                  className={cn(
                    "glass-input h-10 w-full justify-between px-3 font-normal",
                    !employee && "text-muted-foreground",
                  )}
                >
                  {employee ? (
                    <span className="flex items-center gap-2 truncate">
                      <Avatar className="h-5 w-5 text-[8px]">
                        {employee.photoDataUrl ? (
                          <AvatarImage src={employee.photoDataUrl} alt={employeeDisplayName(employee)} />
                        ) : null}
                        <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                          {employeeInitials(employee)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{employeeLabel(employee)}</span>
                    </span>
                  ) : (
                    <span className="truncate text-left">Select employee…</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                <Command key={employeePickerCycle} shouldFilter>
                  <CommandInput placeholder="Search name, code or department…" className="h-9" />
                  <CommandList>
                    <CommandEmpty>No employee found.</CommandEmpty>
                    <CommandGroup>
                      {activeEmployees.map((e) => {
                        const label = employeeLabel(e);
                        const subtitle = employeeSubtitle(e);
                        const selected = selectedEmployeeId === e.id;
                        const searchValue = [label, e.employeeCode, e.departmentName, e.designationName, e.id]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <CommandItem
                            key={e.id}
                            value={searchValue}
                            onSelect={() => {
                              setSelectedEmployeeId(e.id);
                              setEmployee(e);
                              setEmployeePickerOpen(false);
                            }}
                            className="gap-2"
                          >
                            <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                            <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                              {e.photoDataUrl ? <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} /> : null}
                              <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                {employeeInitials(e)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col min-w-0">
                              <span className="truncate text-sm font-medium">{label}</span>
                              {subtitle ? (
                                <span className="truncate text-[11px] opacity-60 leading-tight">{subtitle}</span>
                              ) : null}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        ) : null}

        {employee ? (
          <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs overflow-hidden shrink-0">
                {employee.photoDataUrl ? (
                  <img src={employee.photoDataUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  employeeInitial
                )}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-foreground truncate">{employeeName}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {employee.designationName || "Staff"} · {employee.employeeCode || "—"}
                </p>
              </div>
            </div>
            <div className="text-right shrink-0 pl-3">
              <p className="text-[10px] uppercase font-semibold text-muted-foreground">Basic Monthly Salary</p>
              <p className="font-bold text-primary">{formatCurrency(Number.isFinite(basicSalaryNum) ? basicSalaryNum : 0)}</p>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground flex justify-between items-center">
                <span>
                  Sanctioned Loan Amount <span className="text-destructive">*</span>
                </span>
                {loanNum ? <span className="text-xs text-emerald-600 font-bold">Rs. {commaFormat(loanNum)}</span> : null}
              </Label>
              <div className="relative">
                <Input
                  className="glass-input h-10 pr-12 font-semibold"
                  value={draft.loanAmount}
                  onChange={(e) => setDraft((d) => ({ ...d, loanAmount: formatMoney(e.target.value) }))}
                  placeholder="e.g. 50,000"
                  inputMode="decimal"
                  disabled={!!editing}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  PKR
                </span>
              </div>
              {!editing ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="text-[10px] text-muted-foreground self-center mr-1">Quick:</span>
                {[25000, 50000, 100000].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyAmountPreset(preset)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                  >
                    +{commaFormat(preset)}
                  </button>
                ))}
                {basicSalaryNum > 0 ? (
                  <button
                    type="button"
                    onClick={() => applyAmountPreset(basicSalaryNum)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    1x Salary
                  </button>
                ) : null}
              </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">Loan amount is fixed after creation.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground flex justify-between items-center">
                <span>
                  Monthly Installment <span className="text-destructive">*</span>
                </span>
                {instNum ? <span className="text-xs text-rose-600 font-bold">-{formatCurrency(instNum)}/mo</span> : null}
              </Label>
              <div className="relative">
                <Input
                  className="glass-input h-10 pr-12 font-semibold"
                  value={draft.installmentAmount}
                  onChange={(e) => setDraft((d) => ({ ...d, installmentAmount: formatMoney(e.target.value) }))}
                  placeholder="e.g. 5,000"
                  inputMode="decimal"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  PKR
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <span className="text-[10px] text-muted-foreground self-center mr-1">Tenure:</span>
                {[3, 6, 12, 24].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => applyTenurePreset(m)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                  >
                    {m} Mo
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">
                First Installment Start Date <span className="text-destructive">*</span>
              </Label>
              <DatePartsPicker
                value={draft.startDate}
                onChange={(iso) => setDraft((x) => ({ ...x, startDate: iso }))}
                fromYear={1980}
                toYear={new Date().getFullYear() + 5}
                placeholder="Select start date"
              />
            </div>
          </div>

          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                <Calculator className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Live Schedule Preview</h4>
              </div>

              <div className="space-y-3 pt-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Sanctioned Amount</span>
                  <span className="font-bold text-foreground text-sm">{loanNum ? formatCurrency(loanNum) : "—"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Monthly Deduction</span>
                  <span className="font-bold text-rose-600">{instNum ? `-${formatCurrency(instNum)}` : "—"}</span>
                </div>
                <div className="flex justify-between items-center border-t border-primary/10 pt-2">
                  <span className="text-muted-foreground">Total Installments</span>
                  <span className="font-extrabold text-primary text-sm">
                    {installmentsCount ? `${installmentsCount} Months` : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Estimated Payoff</span>
                  <span className="font-bold text-foreground">{completionDateFormatted || "—"}</span>
                </div>

                {installmentsCount && loanNum && instNum ? (
                  <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-2 mt-2">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-muted-foreground">Monthly Deduction Impact</span>
                      <span className="text-primary">
                        {((instNum / (basicSalaryNum || loanNum)) * 100).toFixed(1)}% of basic
                      </span>
                    </div>
                    <div className="h-2 w-full bg-primary/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${Math.min(100, Math.round((instNum / (basicSalaryNum || loanNum)) * 100))}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic text-center pt-0.5">
                      Deducted automatically from monthly salary payslips starting{" "}
                      {isValid(parseISO(draft.startDate)) ? format(parseISO(draft.startDate), "MMM yyyy") : "—"}.
                    </p>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                    Enter loan and installment amounts to view live schedule breakdown.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
              <Info className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>Loan balance updates automatically as payroll batches are finalized.</span>
            </div>
          </div>
        </div>

        <LoanPaymentMethodFields
          value={draft}
          banks={banks}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        />

        <DialogFooter className="pt-4 border-t border-border/50 gap-2">
          <Button variant="outline" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || !selectedEmployeeId || !loanNum || !instNum}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {editing ? "Saving..." : "Sanctioning Loan..."}
              </>
            ) : editing ? (
              "Save Loan"
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Sanction Loan
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
